import { collection, deleteDoc, doc, onSnapshot, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase/client";
import type { ReactionType, UsePostReactions } from "../types/reactions.types";

const EMOJI_BY_REACTION: Record<ReactionType, string> = {
	thumbs_up: "👍",
	thumbs_down: "👎",
};

const reactionForEmoji = (emoji: string | undefined): ReactionType | null =>
	(Object.keys(EMOJI_BY_REACTION) as ReactionType[]).find(
		(type) => EMOJI_BY_REACTION[type] === emoji,
	) ?? null;

export function usePostReactions(
	broadcastId: string | null,
	postId: string,
	uid: string | null,
): UsePostReactions {
	const [reactions, setReactions] = useState<Record<string, string>>({});

	useEffect(() => {
		if (!broadcastId) return;

		const ref = collection(
			db,
			"broadcasts",
			broadcastId,
			"posts",
			postId,
			"reactions",
		);

		return onSnapshot(ref, (snapshot) => {
			const data: Record<string, string> = {};
			snapshot.docs.forEach((d) => {
				data[d.id] = d.data().emoji;
			});
			setReactions(data);
		});
	}, [broadcastId, postId]);

	const counts = Object.values(reactions).reduce<Record<ReactionType, number>>(
		(acc, emoji) => {
			const type = reactionForEmoji(emoji);
			if (type) acc[type] += 1;
			return acc;
		},
		{ thumbs_up: 0, thumbs_down: 0 },
	);

	const myReaction = uid ? reactionForEmoji(reactions[uid]) : null;

	const react = (type: ReactionType) => {
		if (!broadcastId || !uid) return;

		const ref = doc(
			db,
			"broadcasts",
			broadcastId,
			"posts",
			postId,
			"reactions",
			uid,
		);

		if (myReaction === type) {
			deleteDoc(ref);
		} else {
			setDoc(ref, { emoji: EMOJI_BY_REACTION[type] });
		}
	};

	return { counts, myReaction, react };
}
